import { useEffect, useRef, useState } from "react";
import { Flower2, Loader2, Send, X } from "lucide-react";
import { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
const newSession = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);

async function streamReply(sessionId, message, lang, onDelta) {
  const res = await fetch(`${API}/chat/stream`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session_id: sessionId, message, lang }),
  });
  if (!res.ok || !res.body) throw new Error("chat failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      const data = ev.replace(/^data: /, "");
      if (data === "[DONE]") return;
      try { onDelta(JSON.parse(data).delta); } catch {}
    }
  }
}

export default function PranaChat() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(newSession);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [ended, setEnded] = useState(false);
  const [ending, setEnding] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msgs, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || busy || ended) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    const append = (d) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = { ...c[c.length - 1], text: c[c.length - 1].text + d }; return c; });
    try {
      await streamReply(sessionId, text, lang, append);
    } catch {
      append(t("pr.err"));
    } finally {
      setBusy(false);
    }
  };

  const endChat = async () => {
    setEnding(true);
    try {
      await fetch(`${API}/chat/end`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session_id: sessionId }) });
    } catch {}
    setEnding(false);
    setEnded(true);
  };

  const restart = () => {
    setSessionId(newSession());
    setMsgs([]);
    setEnded(false);
  };

  const hasUserMsg = msgs.some((m) => m.role === "user");

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-forest-800 text-parchment pl-4 pr-5 py-3.5 rounded-full shadow-2xl shadow-forest-900/30 hover:bg-forest-700 transition-colors rise" data-testid="prana-open-button">
          <Flower2 className="w-5 h-5 text-brass-300" /> <span className="font-semibold text-sm">{t("pr.open")}</span>
        </button>
      )}
      {open && (
        <div className="fixed inset-0 sm:inset-auto sm:bottom-5 sm:right-5 z-50 sm:w-[380px] sm:h-[580px] bg-parchment sm:rounded-3xl shadow-2xl border border-parchment-300 flex flex-col overflow-hidden rise" data-testid="prana-chat-window">
          <div className="bg-forest-800 text-parchment px-5 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brass-500 grid place-items-center"><Flower2 className="w-5 h-5 text-forest-900" /></div>
            <div className="flex-1">
              <p className="font-serif text-xl font-bold leading-none">Prana</p>
              <p className="text-xs text-parchment/70 mt-1">{t("pr.sub")}</p>
            </div>
            <button onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-parchment/10" data-testid="prana-close-button" aria-label="Close chat"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="prana-messages">
            {[{ role: "assistant", text: t("pr.greet") }, ...msgs].map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${m.role === "user" ? "bg-forest-800 text-parchment rounded-2xl rounded-br-md" : "bg-white border border-parchment-300 text-forest-900 rounded-2xl rounded-bl-md"}`} data-testid={`prana-msg-${m.role}`}>
                  {m.text || <Loader2 className="w-4 h-4 animate-spin text-brass-700" />}
                </div>
              </div>
            ))}
            {ended && (
              <div className="text-center bg-forest-50 border border-forest-700/20 rounded-xl p-4 text-sm text-forest-800" data-testid="prana-ended-note">
                {t("pr.ended")}
                <button onClick={restart} className="block mx-auto mt-2 text-brass-700 font-semibold underline underline-offset-4" data-testid="prana-restart-button">{t("pr.restart")}</button>
              </div>
            )}
            <div ref={endRef} />
          </div>
          {!ended && (
            <div className="border-t border-parchment-300 p-3 space-y-2 bg-white">
              <form onSubmit={send} className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)} placeholder={t("pr.ph")} maxLength={1000} className="flex-1 bg-parchment rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brass-500" data-testid="prana-input" />
                <button disabled={busy || !input.trim()} className="w-10 h-10 rounded-full bg-forest-800 text-parchment grid place-items-center disabled:opacity-50" data-testid="prana-send-button" aria-label="Send"><Send className="w-4 h-4" /></button>
              </form>
              {hasUserMsg && (
                <button onClick={endChat} disabled={busy || ending} className="w-full text-xs font-semibold text-brass-700 py-1.5 flex items-center justify-center gap-1.5 disabled:opacity-50" data-testid="prana-end-chat-button">
                  {ending && <Loader2 className="w-3 h-3 animate-spin" />} {t("pr.end")}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
}
